import { useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { Plus, Trash2, Calculator, AlertCircle } from 'lucide-react';
import { FAQSchema } from '@/components/schema/FAQSchema';
import { BreadcrumbSchema } from '@/components/schema/BreadcrumbSchema';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const WAULTCalculator = () => {
  const [mieterList, setMieterList] = useState([
    {
      bezeichnung: '',
      jahresmiete: '',
      restlaufzeit_jahre: '',
      enddatum: '',
      optionen_jahre: '',
    },
  ]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [useEnddatum, setUseEnddatum] = useState(false);

  const addMieter = () => {
    setMieterList([
      ...mieterList,
      {
        bezeichnung: '',
        jahresmiete: '',
        restlaufzeit_jahre: '',
        enddatum: '',
        optionen_jahre: '',
      },
    ]);
  };

  const removeMieter = (index) => {
    if (mieterList.length > 1) {
      setMieterList(mieterList.filter((_, i) => i !== index));
    }
  };

  const updateMieter = (index, field, value) => {
    const updated = [...mieterList];
    updated[index][field] = value;
    setMieterList(updated);
  };

  const calculateWAULT = async () => {
    // Validate input
    const hasInvalidMieter = mieterList.some((m) => {
      const hasJahresmiete = m.jahresmiete && parseFloat(m.jahresmiete) > 0;
      const hasRestlaufzeit = m.restlaufzeit_jahre && parseFloat(m.restlaufzeit_jahre) >= 0;
      const hasEnddatum = m.enddatum && m.enddatum.trim() !== '';

      if (!hasJahresmiete) return true;
      if (!hasRestlaufzeit && !hasEnddatum) return true;
      return false;
    });

    if (hasInvalidMieter) {
      toast.error('Bitte füllen Sie für jeden Mieter Jahresmiete und entweder Restlaufzeit oder Enddatum aus.');
      return;
    }

    setLoading(true);
    try {
      // Convert to API format
      const payload = mieterList.map((m) => ({
        bezeichnung: m.bezeichnung || null,
        jahresmiete: parseFloat(m.jahresmiete),
        restlaufzeit_jahre: m.restlaufzeit_jahre ? parseFloat(m.restlaufzeit_jahre) : null,
        enddatum: m.enddatum || null,
        optionen_jahre: m.optionen_jahre ? parseFloat(m.optionen_jahre) : 0,
      }));

      const response = await axios.post(`${API}/tool/wault`, payload);
      setResult(response.data);
      toast.success('WAULT erfolgreich berechnet!');
    } catch (error) {
      console.error('Error calculating WAULT:', error);
      toast.error(error.response?.data?.detail || 'Fehler bei der Berechnung');
    } finally {
      setLoading(false);
    }
  };

  const getAmpelColor = (ampel) => {
    if (ampel === 'gruen') return 'var(--success-600)';
    if (ampel === 'gelb') return 'var(--warning-600)';
    return 'var(--error-600)';
  };

  const getAmpelBg = (ampel) => {
    if (ampel === 'gruen') return 'var(--success-100)';
    if (ampel === 'gelb') return 'var(--warning-100)';
    return 'var(--error-100)';
  };

  const faqData = [
    {
      question: "Welcher WAULT-Wert ist gut?",
      answer: "Ein WAULT \u00fcber 8 Jahren gilt als sehr gut und signalisiert hohe Stabilit\u00e4t. Werte zwischen 5-8 Jahren sind solide. Unter 5 Jahren kann der Verkauf schwieriger werden, da K\u00e4ufer ein h\u00f6heres Risiko sehen."
    },
    {
      question: "Sollte ich Optionen einbeziehen?",
      answer: "Optionen sind rechtlich nicht bindend und k\u00f6nnen vom Mieter nicht ausge\u00fcbt werden. Konservative K\u00e4ufer bewerten nur den WAULT ohne Optionen. F\u00fcr eine realistische Einsch\u00e4tzung sollten Sie beide Werte kennen."
    },
    {
      question: "Was kann ich bei einem niedrigen WAULT tun?",
      answer: "Bei einem WAULT unter 5 Jahren sollten Sie vor dem Verkauf Mietvertragsverl\u00e4ngerungen anstreben. Eine Verl\u00e4ngerung um nur 3 Jahre kann den Verkaufspreis deutlich erh\u00f6hen."
    },
    {
      question: "Wie oft sollte ich den WAULT aktualisieren?",
      answer: "Der WAULT \u00e4ndert sich laufend durch die Zeit. Aktualisieren Sie die Berechnung mindestens einmal j\u00e4hrlich oder bei \u00c4nderungen der Mietvertr\u00e4ge (Verl\u00e4ngerungen, neue Mieter)."
    },
    {
      question: "Ber\u00fccksichtigt der WAULT die Bonit\u00e4t der Mieter?",
      answer: "Nein, der WAULT ist eine rein zeitliche Kennzahl. Die Bonit\u00e4t der Mieter (Tenant Quality) wird separat bewertet und ist ebenfalls sehr wichtig. Ein langer WAULT mit bonit\u00e4tsstarken Mietern (z.B. EDEKA, ALDI) ist ideal."
    }
  ];

  const breadcrumbItems = [
    { name: "Home", url: `${window.location.origin}/` },
    { name: "Tools", url: `${window.location.origin}/tools` },
    { name: "WAULT Rechner", url: `${window.location.origin}/tools/wault` }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <FAQSchema faqs={faqData} />
      <BreadcrumbSchema items={breadcrumbItems} />
      <SEO
        title="WAULT Rechner – Kostenlos Mietlaufzeit berechnen"
        description="Berechnen Sie kostenlos den WAULT (Weighted Average Unexpired Lease Term) Ihrer Handelsimmobilie. Unterstützt mehrere Mieter, Jahre oder Enddatum, mit/ohne Optionen."
        url="/tools/wault"
      />
      <Navbar />
      <main className="flex-grow bg-gray-50 py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1
              className="text-4xl md:text-5xl font-semibold mb-4"
              style={{ color: 'var(--primary-800)' }}
            >
              WAULT Rechner
            </h1>
            <p className="text-lg" style={{ color: 'var(--secondary-600)' }}>
              Berechnen Sie kostenlos den Weighted Average Unexpired Lease Term Ihrer Handelsimmobilie
            </p>
          </div>

          {/* Input Section */}
          <Card className="p-8 mb-8" data-testid="wault-input-card">
            <div className="flex items-center justify-between mb-6">
              <h2
                className="text-2xl font-semibold"
                style={{ color: 'var(--primary-700)' }}
              >
                Mieter-Eingabe
              </h2>
              <Button
                onClick={addMieter}
                data-testid="add-mieter-button"
                variant="default"
                className="font-semibold bg-gradient-to-r from-[#D4A853] to-[#E5B960] text-white hover:shadow-xl transition-all duration-300"
              >
                <Plus className="h-4 w-4 mr-2" />
                Mieter hinzufügen
              </Button>
            </div>

            <div className="space-y-6">
              {mieterList.map((mieter, index) => (
                <div
                  key={index}
                  className="p-6 bg-gray-50 rounded-lg relative"
                  data-testid={`mieter-input-${index}`}
                >
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold" style={{ color: 'var(--primary-600)' }}>
                      Mieter {index + 1}
                    </h3>
                    {mieterList.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeMieter(index)}
                        data-testid={`remove-mieter-${index}`}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor={`bezeichnung-${index}`}>Bezeichnung (optional)</Label>
                      <Input
                        id={`bezeichnung-${index}`}
                        data-testid={`bezeichnung-input-${index}`}
                        placeholder="z.B. Edeka"
                        value={mieter.bezeichnung}
                        onChange={(e) => updateMieter(index, 'bezeichnung', e.target.value)}
                      />
                    </div>

                    <div>
                      <Label htmlFor={`jahresmiete-${index}`}>
                        Jahresmiete (EUR) <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id={`jahresmiete-${index}`}
                        data-testid={`jahresmiete-input-${index}`}
                        type="number"
                        placeholder="100000"
                        value={mieter.jahresmiete}
                        onChange={(e) => updateMieter(index, 'jahresmiete', e.target.value)}
                      />
                    </div>

                    <div>
                      <Label htmlFor={`restlaufzeit-${index}`}>
                        Restlaufzeit (Jahre)
                      </Label>
                      <Input
                        id={`restlaufzeit-${index}`}
                        data-testid={`restlaufzeit-input-${index}`}
                        type="number"
                        step="0.1"
                        placeholder="10"
                        value={mieter.restlaufzeit_jahre}
                        onChange={(e) => updateMieter(index, 'restlaufzeit_jahre', e.target.value)}
                        disabled={!!mieter.enddatum}
                      />
                    </div>

                    <div>
                      <Label htmlFor={`enddatum-${index}`}>
                        Oder Enddatum
                      </Label>
                      <Input
                        id={`enddatum-${index}`}
                        data-testid={`enddatum-input-${index}`}
                        type="date"
                        value={mieter.enddatum}
                        onChange={(e) => updateMieter(index, 'enddatum', e.target.value)}
                        disabled={!!mieter.restlaufzeit_jahre}
                      />
                    </div>

                    <div className="md:col-span-2">
                      <Label htmlFor={`optionen-${index}`}>
                        Optionen (Jahre, optional)
                      </Label>
                      <Input
                        id={`optionen-${index}`}
                        data-testid={`optionen-input-${index}`}
                        type="number"
                        step="0.1"
                        placeholder="5"
                        value={mieter.optionen_jahre}
                        onChange={(e) => updateMieter(index, 'optionen_jahre', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex items-start p-4 bg-blue-50 rounded-lg">
              <AlertCircle className="h-5 w-5 mt-0.5 mr-3" style={{ color: 'var(--primary-500)' }} />
              <p className="text-sm" style={{ color: 'var(--secondary-700)' }}>
                Geben Sie entweder die <strong>Restlaufzeit in Jahren</strong> oder das{' '}
                <strong>Enddatum</strong> des Mietvertrags ein. Optionen sind optional.
              </p>
            </div>

            <Button
              onClick={calculateWAULT}
              disabled={loading}
              data-testid="calculate-wault-button"
              variant="default"
              className="w-full mt-6 py-6 text-lg font-medium bg-[#2563A0] text-white hover:bg-[#1B4D7A] transition-all duration-300"
            >
              <Calculator className="h-5 w-5 mr-2" />
              {loading ? 'Berechne...' : 'WAULT berechnen'}
            </Button>
          </Card>

          {/* Explanatory Content Section */}
          <Card className="p-8 mb-8 bg-[#EFF6FF]" data-testid="wault-info-card">
            <h2 className="text-2xl font-semibold mb-4" style={{ color: 'var(--primary-700)' }}>
              Was ist der WAULT?
            </h2>
            <p className="text-base mb-4" style={{ color: 'var(--secondary-700)' }}>
              Der <strong>WAULT (Weighted Average Unexpired Lease Term)</strong> ist eine der wichtigsten Kennzahlen 
              im gewerblichen Immobilienbereich. Er gibt die durchschnittliche Restlaufzeit aller Mietverträge an, 
              gewichtet nach der jeweiligen Jahresmiete. Ein hoher WAULT bedeutet langfristige Mieteinnahmen und 
              damit höhere Sicherheit für Investoren.
            </p>
            <h3 className="text-xl font-semibold mb-3 mt-6" style={{ color: 'var(--primary-700)' }}>
              Warum ist der WAULT wichtig?
            </h3>
            <ul className="space-y-2 mb-6">
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full mt-2" style={{ backgroundColor: 'var(--gold-600)' }}></div>
                <p style={{ color: 'var(--secondary-700)' }}>
                  <strong>Verkaufspreis:</strong> Ein WAULT über 8 Jahren kann den Verkaufspreis um 10-20% steigern
                </p>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full mt-2" style={{ backgroundColor: 'var(--gold-600)' }}></div>
                <p style={{ color: 'var(--secondary-700)' }}>
                  <strong>Finanzierung:</strong> Banken bewerten Objekte mit längerem WAULT positiver
                </p>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full mt-2" style={{ backgroundColor: 'var(--gold-600)' }}></div>
                <p style={{ color: 'var(--secondary-700)' }}>
                  <strong>Investorenpräferenz:</strong> Institutionelle Käufer bevorzugen WAULT-Werte von 7+ Jahren
                </p>
              </li>
            </ul>
            <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--primary-700)' }}>
              Wie wird der WAULT berechnet?
            </h3>
            <p className="text-base" style={{ color: 'var(--secondary-700)' }}>
              Der WAULT wird berechnet, indem die Restlaufzeit jedes Mietvertrags mit der entsprechenden Jahresmiete 
              multipliziert und durch die Gesamtjahresmiete geteilt wird. Optionen werden separat berücksichtigt.
            </p>
          </Card>

          {/* FAQ Section */}
          <Card className="p-8 mb-8" data-testid="wault-faq-card">
            <h2 className="text-2xl font-semibold mb-6" style={{ color: 'var(--primary-700)' }}>
              Häufig gestellte Fragen (FAQ)
            </h2>
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="faq-1" className="border rounded-lg px-4">
                <AccordionTrigger className="text-left font-semibold" style={{ color: 'var(--primary-700)' }}>
                  Welcher WAULT-Wert ist gut?
                </AccordionTrigger>
                <AccordionContent className="text-base" style={{ color: 'var(--secondary-700)' }}>
                  Ein WAULT über 8 Jahren gilt als sehr gut und signalisiert hohe Stabilität. 
                  Werte zwischen 5-8 Jahren sind solide. Unter 5 Jahren kann der Verkauf schwieriger werden, 
                  da Käufer ein höheres Risiko sehen.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="faq-2" className="border rounded-lg px-4">
                <AccordionTrigger className="text-left font-semibold" style={{ color: 'var(--primary-700)' }}>
                  Sollte ich Optionen einbeziehen?
                </AccordionTrigger>
                <AccordionContent className="text-base" style={{ color: 'var(--secondary-700)' }}>
                  Optionen sind rechtlich nicht bindend und können vom Mieter nicht ausgeübt werden. 
                  Konservative Käufer bewerten nur den WAULT ohne Optionen. Für eine realistische Einschätzung 
                  sollten Sie beide Werte kennen.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="faq-3" className="border rounded-lg px-4">
                <AccordionTrigger className="text-left font-semibold" style={{ color: 'var(--primary-700)' }}>
                  Was kann ich bei einem niedrigen WAULT tun?
                </AccordionTrigger>
                <AccordionContent className="text-base" style={{ color: 'var(--secondary-700)' }}>
                  Bei einem WAULT unter 5 Jahren sollten Sie vor dem Verkauf Mietvertragsverlängerungen 
                  anstreben. Eine Verlängerung um nur 3 Jahre kann den Verkaufspreis deutlich erhöhen. 
                  Wir beraten Sie gerne zur optimalen Verkaufsstrategie.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="faq-4" className="border rounded-lg px-4">
                <AccordionTrigger className="text-left font-semibold" style={{ color: 'var(--primary-700)' }}>
                  Wie oft sollte ich den WAULT aktualisieren?
                </AccordionTrigger>
                <AccordionContent className="text-base" style={{ color: 'var(--secondary-700)' }}>
                  Der WAULT ändert sich laufend durch die Zeit. Aktualisieren Sie die Berechnung mindestens 
                  einmal jährlich oder bei Änderungen der Mietverträge (Verlängerungen, neue Mieter).
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="faq-5" className="border rounded-lg px-4">
                <AccordionTrigger className="text-left font-semibold" style={{ color: 'var(--primary-700)' }}>
                  Berücksichtigt der WAULT die Bonität der Mieter?
                </AccordionTrigger>
                <AccordionContent className="text-base" style={{ color: 'var(--secondary-700)' }}>
                  Nein, der WAULT ist eine rein zeitliche Kennzahl. Die Bonität der Mieter ("Tenant Quality") 
                  wird separat bewertet und ist ebenfalls sehr wichtig. Ein langer WAULT mit bonitätsstarken 
                  Mietern (z.B. EDEKA, ALDI) ist ideal.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </Card>

          {/* Result Section */}
          {result && (
            <Card className="p-8" data-testid="wault-result-card">
              <h2
                className="text-2xl font-semibold mb-6"
                style={{ color: 'var(--primary-700)' }}
              >
                Ergebnis
              </h2>

              {/* WAULT Values */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div
                  className="p-6 rounded-lg"
                  style={{ backgroundColor: 'var(--primary-50)' }}
                  data-testid="wault-ohne-optionen"
                >
                  <p className="text-sm mb-2" style={{ color: 'var(--secondary-600)' }}>
                    WAULT ohne Optionen
                  </p>
                  <p
                    className="text-4xl font-bold"
                    style={{ color: 'var(--primary-700)' }}
                  >
                    {result.wault_ohne_optionen} Jahre
                  </p>
                </div>

                <div
                  className="p-6 rounded-lg"
                  style={{ backgroundColor: 'var(--accent-100)' }}
                  data-testid="wault-mit-optionen"
                >
                  <p className="text-sm mb-2" style={{ color: 'var(--secondary-600)' }}>
                    WAULT mit Optionen
                  </p>
                  <p
                    className="text-4xl font-bold"
                    style={{ color: 'var(--accent-900)' }}
                  >
                    {result.wault_mit_optionen} Jahre
                  </p>
                </div>
              </div>

              {/* Ampel */}
              <div
                className="p-6 rounded-lg mb-6"
                style={{ backgroundColor: getAmpelBg(result.ampel) }}
                data-testid="wault-ampel"
              >
                <div className="flex items-center mb-3">
                  <div
                    className="w-4 h-4 rounded-full mr-3"
                    style={{ backgroundColor: getAmpelColor(result.ampel) }}
                  ></div>
                  <h3
                    className="text-xl font-semibold"
                    style={{ color: getAmpelColor(result.ampel) }}
                  >
                    {result.ampel === 'gruen' && 'Sehr gut'}
                    {result.ampel === 'gelb' && 'Solide'}
                    {result.ampel === 'rot' && 'Kritisch'}
                  </h3>
                </div>
                <p className="text-base" style={{ color: 'var(--secondary-700)' }}>
                  {result.interpretation}
                </p>
              </div>

              {/* Empfehlung */}
              <div
                className="p-6 rounded-lg mb-6"
                style={{ backgroundColor: 'var(--secondary-50)' }}
                data-testid="wault-empfehlung"
              >
                <h3
                  className="text-lg font-semibold mb-3"
                  style={{ color: 'var(--primary-700)' }}
                >
                  Empfehlung
                </h3>
                <p className="text-base" style={{ color: 'var(--secondary-700)' }}>
                  {result.empfehlung}
                </p>
              </div>

              {/* Mieter Details */}
              <div className="mb-6">
                <h3
                  className="text-lg font-semibold mb-4"
                  style={{ color: 'var(--primary-700)' }}
                >
                  Mieter-Details
                </h3>
                <div className="space-y-3">
                  {result.mieter_details.map((detail, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center p-4 bg-gray-50 rounded-lg"
                      data-testid={`mieter-detail-${index}`}
                    >
                      <div>
                        <p className="font-semibold" style={{ color: 'var(--primary-700)' }}>
                          {detail.bezeichnung}
                        </p>
                        <p className="text-sm" style={{ color: 'var(--secondary-600)' }}>
                          Restlaufzeit: {detail.restlaufzeit_jahre} Jahre
                          {detail.optionen_jahre > 0 &&
                            ` (+ ${detail.optionen_jahre} Jahre Optionen)`}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold" style={{ color: 'var(--primary-700)' }}>
                          {detail.gewichtung_prozent}%
                        </p>
                        <p className="text-sm" style={{ color: 'var(--secondary-600)' }}>
                          {detail.jahresmiete.toLocaleString('de-DE')} €
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  className="flex-1"
                  data-testid="cta-markt-einordnung"
                  style={{
                    backgroundColor: 'var(--primary-500)',
                    color: 'white',
                  }}
                  onClick={() => (window.location.href = '/kontakt')}
                >
                  Markt-Einordnung anfragen
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  data-testid="cta-termin"
                  style={{
                    borderColor: 'var(--primary-500)',
                    color: 'var(--primary-500)',
                  }}
                  onClick={() => (window.location.href = '/kontakt')}
                >
                  Termin vereinbaren
                </Button>
              </div>
            </Card>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default WAULTCalculator;